/**
 * Single source of truth for admin "account" actions — the edge-function calls
 * behind the user-roles table (Settings) and the Owners list action menu.
 *
 * Both surfaces used to invoke these functions inline with subtly different
 * error handling; this centralises the function names, request-body shapes and
 * (crucially) the error-normalisation so behaviour can't drift between them.
 *
 * These helpers are UI-agnostic: they never toast, navigate or invalidate
 * queries. Each returns a normalised {@link EdgeResult} and the caller decides
 * how to surface it (toast copy, which react-query keys to invalidate, etc.).
 */
import { supabase } from "@/lib/supabase";

/** Normalised outcome of an admin edge-function call. */
export type EdgeResult = {
  ok: boolean;
  /** Success message from the function (if any). */
  message?: string;
  /** Human-readable failure reason (already unwrapped from the error body). */
  error?: string;
};

type EdgeBody = { ok?: boolean; error?: string; message?: string };

/**
 * Invoke an admin edge function and normalise both success and failure.
 *
 * On a non-2xx response, `supabase.functions.invoke` returns a generic
 * FunctionsHttpError and stashes the real JSON body ({ error }) on
 * `error.context` — we unwrap that so domain guards (e.g. "user still owns
 * bookings") surface instead of a generic "Edge Function returned a non-2xx".
 */
async function invokeAdmin(
  fn: string,
  body: Record<string, unknown>
): Promise<EdgeResult> {
  try {
    const { data, error } = await supabase.functions.invoke<EdgeBody>(fn, {
      body,
    });
    if (error) {
      let message = error.message || `Failed to run ${fn}.`;
      const ctx = (
        error as { context?: { json?: () => Promise<{ error?: string }> } }
      ).context;
      if (ctx && typeof ctx.json === "function") {
        try {
          const parsed = await ctx.json();
          if (parsed?.error) message = parsed.error;
        } catch {
          /* keep the generic message */
        }
      }
      return { ok: false, error: message };
    }
    if (data?.error) return { ok: false, error: data.error };
    return { ok: true, message: data?.message };
  } catch (e: unknown) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : `Failed to run ${fn}.`,
    };
  }
}

/** Set (admin-override) a user's password so they can sign in with it immediately. */
export function setUserPassword(
  userId: string,
  password: string
): Promise<EdgeResult> {
  return invokeAdmin("admin-set-user-password", {
    user_id: userId,
    password,
  });
}

/**
 * Email a user a password-reset link.
 *
 * Deliberately NOT AuthContext.resetPasswordForEmail: that helper toasts on the
 * user's behalf ("check your inbox"), which is wrong copy for an admin acting on
 * someone else's account and double-toasts alongside the caller's own message.
 * Same edge function, no UI side effects — like every other helper here.
 *
 * `ok` means the request was accepted, NOT that an email went out:
 * send-password-reset returns success for an unknown address on purpose, so the
 * endpoint can't be used to enumerate accounts. Callers must word their success
 * message so it can't claim more than that.
 */
export function sendPasswordResetLink(opts: {
  email: string;
  /** Where the reset link lands — an absolute URL (getSiteUrlForPath). */
  redirectTo: string;
  language?: "de" | "en";
}): Promise<EdgeResult> {
  return invokeAdmin("send-password-reset", {
    email: opts.email,
    language: opts.language ?? "en",
    redirectTo: opts.redirectTo,
  });
}

/** Activate / deactivate a user's portal access. */
export function setUserActive(
  userId: string,
  isActive: boolean
): Promise<EdgeResult> {
  return invokeAdmin("admin-deactivate-user", {
    user_id: userId,
    is_active: isActive,
  });
}

/** Send an owner-onboarding invitation for one property. */
export function inviteOwnerToProperty(opts: {
  email: string;
  propertyId: number;
  /**
   * Scopes the invitation to ONE apartment. Omit for a property-wide invite.
   * Must belong to `propertyId` — the function rejects a mismatch.
   */
  apartmentId?: number | null;
  name?: string | null;
  contractTemplateIds?: string[];
}): Promise<EdgeResult> {
  return invokeAdmin("admin-invite-owner", {
    email: opts.email,
    property_id: opts.propertyId,
    app_url: window.location.origin,
    ...(opts.apartmentId != null ? { apartment_id: opts.apartmentId } : {}),
    ...(opts.name ? { prefilled_name: opts.name } : {}),
    ...(opts.contractTemplateIds
      ? { contract_template_ids: opts.contractTemplateIds }
      : {}),
  });
}

/** Send a magic sign-in link to a user (no portal account required). */
export function sendUserLoginLink(opts: {
  email: string;
  role?: string;
  name?: string;
}): Promise<EdgeResult> {
  return invokeAdmin("admin-send-login-link", {
    email: opts.email,
    role: opts.role,
    name: opts.name,
    app_url: window.location.origin,
  });
}

/** Permanently delete a user account (subject to server-side guards). */
export function deleteUserAccount(userId: string): Promise<EdgeResult> {
  return invokeAdmin("admin-delete-user", { user_id: userId });
}
